import React, { useState } from 'react';
import styled from 'styled-components';

const ColorPicker = ({ color, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentColor, setCurrentColor] = useState(color || '#000000');
  
  const predefinedColors = [
    '#5876ff', // HTML Stash blue
    '#ffffff', // White
    '#000000', // Black
    '#f44336', // Red
    '#e91e63', // Pink
    '#9c27b0', // Purple
    '#673ab7', // Deep Purple
    '#3f51b5', // Indigo
    '#2196f3', // Blue
    '#03a9f4', // Light Blue
    '#00bcd4', // Cyan
    '#009688', // Teal
    '#4caf50', // Green
    '#8bc34a', // Light Green
    '#cddc39', // Lime
    '#ffeb3b', // Yellow
    '#ffc107', // Amber
    '#ff9800', // Orange
    '#ff5722', // Deep Orange
    '#795548', // Brown
    '#9e9e9e', // Grey
    '#607d8b', // Blue Grey
    'transparent', // Transparent
  ];
  
  const handleColorChange = (e) => {
    const newColor = e.target.value;
    setCurrentColor(newColor);
    onChange(newColor);
  };
  
  const handlePresetClick = (presetColor) => {
    setCurrentColor(presetColor);
    onChange(presetColor);
    setIsOpen(false);
  };
  
  return (
    <ColorPickerContainer>
      <ColorPreview onClick={() => setIsOpen(!isOpen)}>
        <ColorSwatch color={currentColor} />
        <ColorValue>{currentColor}</ColorValue>
        <DropdownIcon className={`fas fa-chevron-${isOpen ? 'up' : 'down'}`} />
      </ColorPreview>
      
      {isOpen && (
        <ColorPickerDropdown>
          <CustomColorInput
            type="color"
            value={currentColor === 'transparent' ? '#ffffff' : currentColor}
            onChange={handleColorChange}
          />
          
          <PresetColors>
            {predefinedColors.map((presetColor) => (
              <PresetColor
                key={presetColor}
                color={presetColor}
                selected={currentColor === presetColor}
                onClick={() => handlePresetClick(presetColor)}
              >
                {presetColor === 'transparent' && <TransparentIcon />}
              </PresetColor>
            ))}
          </PresetColors>
        </ColorPickerDropdown>
      )}
    </ColorPickerContainer>
  );
};

const ColorPickerContainer = styled.div`
  position: relative;
  width: 100%;
`;

const ColorPreview = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px;
  cursor: pointer;
  background-color: #fff;
`;

const ColorSwatch = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 3px;
  background-color: ${props => props.color === 'transparent' ? 
    'repeating-conic-gradient(#e0e0e0 0% 25%, white 0% 50%) 50% / 8px 8px' : 
    props.color};
  border: 1px solid #e0e0e0;
  margin-right: 10px;
`;

const ColorValue = styled.div`
  flex: 1;
  font-size: 14px;
`;

const DropdownIcon = styled.i`
  color: #666;
`;

const ColorPickerDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 10px;
  margin-top: 5px;
  z-index: 10;
`;

const CustomColorInput = styled.input`
  width: 100%;
  height: 30px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin-bottom: 10px;
  cursor: pointer;
`;

const PresetColors = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 5px;
`;

const PresetColor = styled.div`
  width: 100%;
  padding-bottom: 100%;
  border-radius: 3px;
  background-color: ${props => props.color === 'transparent' ? 
    'repeating-conic-gradient(#e0e0e0 0% 25%, white 0% 50%) 50% / 8px 8px' : 
    props.color};
  border: 2px solid ${props => props.selected ? '#5876ff' : 'transparent'};
  cursor: pointer;
  position: relative;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const TransparentIcon = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:after {
    content: '';
    width: 100%;
    height: 2px;
    background-color: #ff5252;
    transform: rotate(45deg);
  }
`;

export default ColorPicker;
